import type { Dispatch, SetStateAction } from 'react';

import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';

import type { ProductHit } from '@libs/algolia';
import {
    ALGOLIA_PERSISTANT_PRODUCT_FILTERS,
    AlgoliaIndice,
    SEARCH_PAGE_PATH,
    createAlgoliaClient,
    isNumericSKU,
    getProductUrl,
} from '@libs/algolia';

import { createRedirectUrlPlugin } from '@algolia/autocomplete-plugin-redirect-url';

import {
    setSearchStats,
    trackLegacySearchEvent,
    trackSearchFlyoutClick,
    trackSubmitSearchEvent,
} from '@libs/analytics';
import { stripHtml, usePageLayout } from '@libs/common-next';
import type { AutocompleteItem, SetAutocompleteStateOptions } from './types';
import { getSectionTitle } from './utils';

type SetAutocompleteState = Dispatch<SetStateAction<SetAutocompleteStateOptions>>;

type AutocompleteQuerySuggestionsHit = {
    query: string;
    __autocomplete_qsCategory?: string;
};

type RecentSearchesItem = {
    label: string;
    category: string;
};

type SuggestedCategoryWithQueryParamsProps = {
    query?: string;
    category?: string;
};

type AssistedUrlTrackingDataProps = {
    searchTerm: string;
    header: string;
    term: string;
};

const searchClient = createAlgoliaClient();
const redirectUrlPlugin = createRedirectUrlPlugin();

export const getSuggestedCategoryWithQuery = (query: string, category?: string): string | null => {
    if (category) {
        return `/${SEARCH_PAGE_PATH}?q=${stripHtml(query.trim())}&category=${encodeURIComponent(category)}&format=new`;
    }

    return `/${SEARCH_PAGE_PATH}?q=${stripHtml(query.trim())}&format=new`;
};

export const getAssistedUrl = (
    pluginId: string,
    term: string,
    item: RecentSearchesItem | AutocompleteQuerySuggestionsHit | AutocompleteItem
) => {
    const suggestedCategoryWithQueryParams: SuggestedCategoryWithQueryParamsProps = {};

    const header = getSectionTitle(pluginId || '').toLowerCase();

    const autocompleteItem = item as AutocompleteItem;
    const searchTerm = autocompleteItem.name ?? (autocompleteItem.label as string) ?? autocompleteItem.objectID;

    const assistedParams = `st=assisted&sa=${header}&sterm=${searchTerm}`;

    switch (pluginId) {
        case 'productSuggestionsPlugin': {
            const isSkuId = isNumericSKU(term);
            const url = getProductUrl(item as ProductHit, isSkuId);
            setSearchStats({
                searchResults: 1,
                searchTerm,
                searchType: 'assisted',
                isSuggestedProduct: true,
            });

            trackSearchFlyoutClick({
                term,
                header,
                url,
            });

            trackSubmitSearchEvent(searchTerm, header, term);
            trackLegacySearchEvent(searchTerm, header);

            return { url };
        }

        case 'querySuggestionsPlugin': {
            const a = item as AutocompleteQuerySuggestionsHit;
            suggestedCategoryWithQueryParams.query = a.query;
            suggestedCategoryWithQueryParams.category = a.__autocomplete_qsCategory;
            break;
        }

        case 'recentSearchesPlugin': {
            const r = item as RecentSearchesItem;
            suggestedCategoryWithQueryParams.query = r.label;
            suggestedCategoryWithQueryParams.category = r.category;
            break;
        }

        default: {
            break;
        }
    }

    const categoryAndQuery = getSuggestedCategoryWithQuery(
        suggestedCategoryWithQueryParams.query || '',
        suggestedCategoryWithQueryParams.category
    );

    const urlParams = `${assistedParams}&sat=${term}`;
    const url = `${categoryAndQuery}&${urlParams}`;

    trackSearchFlyoutClick({
        term,
        header,
        url,
    });

    return {
        url,
        trackingData: {
            searchTerm,
            header,
            term,
        },
    };
};

const onSubmitSuggestion = (e: Event, url: string, trackingData: AssistedUrlTrackingDataProps) => {
    const elem = e.target as Element;
    const isSubmit = !(
        elem.classList.contains('autocomplete-assist-search') ||
        elem.parentElement?.classList.contains('autocomplete-assist-search')
    );

    if (isSubmit) {
        const { searchTerm, header, term } = trackingData;
        trackSubmitSearchEvent(searchTerm, header, term);
        trackLegacySearchEvent(searchTerm, header);
        window.location.href = url;
    }
};

export const onSelectSuggestion =
    (setAutocompleteQuery: Dispatch<SetStateAction<string>>, pluginId: string) =>
    ({ item, event }: { item: AutocompleteQuerySuggestionsHit; event: Event }) => {
        const { query } = item;

        setAutocompleteQuery(prevQuery => {
            const { url, trackingData } = getAssistedUrl(pluginId, prevQuery, item as AutocompleteQuerySuggestionsHit);

            onSubmitSuggestion(event, url, trackingData as AssistedUrlTrackingDataProps);
            return query;
        });
    };

export const useAlgoliaPlugins = (
    setAutocompleteState: SetAutocompleteState,
    setAutocompleteQuery: Dispatch<SetStateAction<string>>,
    productsHitPerPage: number
) => {
    const { flags } = usePageLayout();

    const recentSearches = flags.enableRecentSearches
        ? createLocalStorageRecentSearchesPlugin({
              key: 'instantsearch',
              limit: 3,
              transformSource({ source }) {
                  const pluginId = 'recentSearchesPlugin';
                  return {
                      ...source,
                      sourceId: pluginId,
                      getItems(params) {
                          return source.getItems(params);
                      },
                      onSelect({ item, event }) {
                          const { label } = item;

                          setAutocompleteQuery(prevQuery => {
                              const { url, trackingData } = getAssistedUrl(
                                  pluginId,
                                  prevQuery,
                                  item as RecentSearchesItem
                              );

                              onSubmitSuggestion(event, url, trackingData as AssistedUrlTrackingDataProps);

                              return label;
                          });
                      },
                  };
              },
          })
        : {};

    const querySuggestions = createQuerySuggestionsPlugin({
        searchClient,
        indexName: AlgoliaIndice.PRODUCTS_QUERY_SUGGESTIONS,
        getSearchParams() {
            return { hitsPerPage: 6 };
        },
        categoryAttribute: [AlgoliaIndice.PRODUCTS, 'facets', 'exact_matches', 'primary_category_name'],
        transformSource({ source }) {
            const pluginId = 'querySuggestionsPlugin';
            return {
                ...source,
                sourceId: pluginId,
                onSelect: onSelectSuggestion(setAutocompleteQuery, pluginId),
                getItems(params) {
                    return source.getItems(params);
                },
            };
        },
    });

    const productSuggestions = {
        getSources() {
            const pluginId = 'productSuggestionsPlugin';
            return [
                {
                    sourceId: pluginId,
                    onSelect({ item }: { item: AutocompleteItem }) {
                        if (item.id) {
                            setAutocompleteQuery(prevQuery => {
                                const { url } = getAssistedUrl(pluginId, prevQuery, item);
                                window.location.href = url;
                                return prevQuery;
                            });
                        }
                    },
                    getItems({ query }: { query: string }) {
                        if (!query) {
                            return [];
                        }

                        return getAlgoliaResults({
                            searchClient,
                            queries: [
                                {
                                    indexName: AlgoliaIndice.PRODUCTS,
                                    query,
                                    params: {
                                        hitsPerPage: productsHitPerPage,
                                        filters: ALGOLIA_PERSISTANT_PRODUCT_FILTERS,
                                        facetingAfterDistinct: true,
                                        clickAnalytics: true,
                                    },
                                },
                            ],
                        });
                    },
                },
            ];
        },
    };

    return [recentSearches, querySuggestions, productSuggestions, redirectUrlPlugin];
};
